import {
  ButtonGroup,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  StackProps,
  Tooltip,
} from '@chakra-ui/react';
import { LineItemType } from '@pelicargo/types';
import { modelUtils } from '@pelicargo/utils';
import { FocusEvent, useCallback, useMemo } from 'react';
import { BiSolidGasPump } from 'react-icons/bi';
import { HiShieldCheck, HiTruck } from 'react-icons/hi';
import { IoAirplane } from 'react-icons/io5';

import { trpc } from '../../config/trpc';
import { useLineItems } from '../../hooks/lineItem/useLineItems';
import { useUpdateLineItem } from '../../hooks/lineItem/useUpdate';
import { Quote } from '../../utils/types';
import { ScreeningIcon } from '../Icons/ScreeningIcon';

type Props = StackProps & {
  quoteVariant: Quote['quote_variants'][0];
};

export const QuoteVariantQuickActions = ({ quoteVariant, ...rest }: Props) => {
  const { mutateAsync: deleteLineItem } = trpc.deleteLineItem.useMutation();

  const { lineItems: liveLineItems } = useLineItems(quoteVariant?.id);

  const lineItems = liveLineItems?.length ? liveLineItems : quoteVariant?.line_items;
  const airFreight = lineItems?.find((li) => modelUtils.lineItem.isRateCategory(li.type));
  const fuelLine = lineItems?.find((li) => li.type === LineItemType.FUEL);
  const securityLine = lineItems?.find((li) => li.type === LineItemType.SECURITY);
  const screeningLine = lineItems?.find((li) => li.type === LineItemType.SCREENING);
  const truckingLine = lineItems?.find((li) => li.type === LineItemType.TRUCKING);

  const nukeLineItem = useCallback(
    (lineItem: { id: number }) => async () => deleteLineItem({ id: lineItem.id }),
    [deleteLineItem],
  );

  const airFreightCost = useMemo(() => {
    if (!airFreight) return 0;
    if (airFreight?.is_per_unit) return airFreight.per_unit_cost;
    return 0;
  }, [airFreight]);

  const [updateLineItem, isLoading] = useUpdateLineItem(airFreight?.id);

  const updateCost = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const cost = parseFloat(event.target.value);
      if (isNaN(cost)) return;
      updateLineItem({ id: airFreight?.id, per_unit_cost: cost });
    },
    [airFreight?.id, updateLineItem],
  );

  return (
    <HStack {...rest}>
      <InputGroup size="sm">
        <InputLeftAddon>
          <IoAirplane />
        </InputLeftAddon>
        <Input
          w="4em"
          px="2"
          onBlur={updateCost}
          isDisabled={!airFreight?.is_per_unit || isLoading}
          defaultValue={airFreightCost / 100}
        />
      </InputGroup>
      <ButtonGroup isAttached spacing={0}>
        <Tooltip label={fuelLine ? 'Remove Fuel' : undefined}>
          <IconButton
            isDisabled={!fuelLine}
            colorScheme="gray"
            onClick={nukeLineItem(fuelLine)}
            icon={<BiSolidGasPump />}
            aria-label="Remove Fuel"
            size="sm"
          />
        </Tooltip>

        <Tooltip label={securityLine ? 'Remove Security' : undefined}>
          <IconButton
            isDisabled={!securityLine}
            colorScheme="gray"
            onClick={nukeLineItem(securityLine)}
            icon={<HiShieldCheck />}
            aria-label="Remove Security"
            size="sm"
          />
        </Tooltip>

        <Tooltip label={screeningLine ? 'Remove Screening' : undefined}>
          <IconButton
            isDisabled={!screeningLine}
            colorScheme="gray"
            onClick={nukeLineItem(screeningLine)}
            icon={<ScreeningIcon />}
            aria-label="Remove Screening"
            size="sm"
          />
        </Tooltip>

        <Tooltip label={truckingLine ? 'Remove Trucking' : undefined}>
          <IconButton
            isDisabled={!truckingLine}
            colorScheme="gray"
            onClick={nukeLineItem(truckingLine)}
            icon={<HiTruck />}
            aria-label="Remove Trucking"
            size="sm"
          />
        </Tooltip>
      </ButtonGroup>
    </HStack>
  );
};
